import CryptoJS from 'crypto-js';
import { getCsrfToken } from './csrfTokenCache';
import { devLog, devWarn } from '../utils/devLog';

// Authentication Service with JWT token management
class AuthService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production'
      ? 'https://blp-be.onrender.com'
      : 'http://localhost:5000');

    this.tokenKey = 'bl_access_token';
    this.refreshTokenKey = 'bl_refresh_token';
    this.userKey = 'bl_user_data';
    
    // Use environment variable or generate session-specific key
    this.encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY || this.generateSessionKey();
    
    // Generate browser fingerprint for token binding
    this.browserFingerprint = this.generateBrowserFingerprint();
    
    // Initialize token refresh interval (reduced to 5 minutes)
    this.initializeTokenRefresh();
  }

  // Encrypt sensitive data before storing
  encrypt(data) {
    return CryptoJS.AES.encrypt(JSON.stringify(data), this.encryptionKey).toString();
  }

  // Decrypt stored data
  decrypt(encryptedData) {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, this.encryptionKey);
      return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (error) {
      return null;
    }
  }

  // Secure token storage
  setTokens(accessToken, refreshToken, userData = null) {
    if (accessToken) {
      localStorage.setItem(this.tokenKey, this.encrypt(accessToken));
    }
    if (refreshToken) {
      localStorage.setItem(this.refreshTokenKey, this.encrypt(refreshToken));
    }
    if (userData) {
      localStorage.setItem(this.userKey, this.encrypt(userData));
    }
  }

  // Retrieve and decrypt tokens
  getAccessToken() {
    const encryptedToken = localStorage.getItem(this.tokenKey);
    return encryptedToken ? this.decrypt(encryptedToken) : null;
  }

  getRefreshToken() {
    const encryptedToken = localStorage.getItem(this.refreshTokenKey);
    return encryptedToken ? this.decrypt(encryptedToken) : null;
  }

  getUserData() {
    const encryptedData = localStorage.getItem(this.userKey);
    return encryptedData ? this.decrypt(encryptedData) : null;
  }

  // Clear all authentication data
  clearTokens() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.userKey);
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getAccessToken();
    if (!token) return false;
    
    try {
      // Basic JWT structure validation
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      
      // Decode payload to check expiration
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      return payload.exp > currentTime;
    } catch (error) {
      return false;
    }
  }

  // Delegate to the shared CSRF cache so apiService and authService stay
  // in sync about which token matches the current cookie.
  async getCsrfToken(opts = {}) {
    return getCsrfToken(opts);
  }

  // Get authentication headers for API requests
  getAuthHeaders() {
    const token = this.getAccessToken();
    return token ? {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    } : {
      'Content-Type': 'application/json'
    };
  }

  // Hash password for secure transmission
  hashPassword(password, email) {
    const salt = CryptoJS.SHA256(email + 'bluelagoon_secure_salt_2024').toString();
    const hashedPassword = CryptoJS.PBKDF2(password, salt, {
      keySize: 256/32,
      iterations: 10000
    }).toString();
    
    return hashedPassword;
  }

  // Handle API responses with token refresh
  async handleApiResponse(response) {
    if (response.status === 401) {
      const refreshToken = this.getRefreshToken();
      if (refreshToken) {
        const refreshResult = await this.refreshAccessToken();
        if (refreshResult.success) {
          // Retry original request with new token
          return { shouldRetry: true };
        }
      }
      // Token refresh failed, clear tokens
      this.clearTokens();
      return { shouldRetry: false, error: 'Authentication expired' };
    }
    return { shouldRetry: false };
  }

  // Main API call method with JWT authentication
  async apiCall(endpoint, options = {}, maxRetries = 3) {
    const url = `${this.baseURL}${endpoint}`;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const requestOptions = {
        ...options,
        credentials: 'include',
        headers: {
          ...this.getAuthHeaders(),
          'User-Agent': 'BluelagoonApp/2.0.0',
          'X-Client-Version': '2.0.0',
          'X-Request-ID': this.generateRequestId(),
          ...options.headers
        }
      };

      // Attach CSRF token to state-changing requests
      const reqMethod = (options.method || 'GET').toUpperCase();
      if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(reqMethod)) {
        try {
          const token = await this.getCsrfToken();
          if (token) requestOptions.headers['X-CSRF-Token'] = token;
        } catch (e) {
          devWarn('CSRF token fetch failed:', e.message);
        }
      }

      try {
        devLog(`API Call: ${options.method || 'GET'} ${endpoint}`);

        const response = await fetch(url, requestOptions);

        // Handle authentication errors
        const authResult = await this.handleApiResponse(response);
        if (authResult.shouldRetry && attempt < maxRetries) {
          continue;
        }

        // Handle CSRF token expiration — refresh once and retry
        if (response.status === 403 && attempt === 0) {
          try {
            const errBody = await response.clone().json();
            if (errBody?.error?.code === 'CSRF_TOKEN_INVALID') {
              devLog('Token invalid — refreshing and retrying');
              await this.getCsrfToken({ forceRefresh: true });
              continue;
            }
          } catch {
            // not JSON or no error code — fall through
          }
        }

        devLog(`Response: ${response.status} ${response.statusText}`);
        return response;
        
      } catch (error) {
        console.error(`API call failed (attempt ${attempt + 1}):`, error);
        
        if (attempt === maxRetries) {
          throw new Error(`API call failed after ${maxRetries + 1} attempts: ${error.message}`);
        }
        
        // Exponential backoff
        await new Promise(resolve => 
          setTimeout(resolve, Math.pow(2, attempt) * 1000 + Math.random() * 1000)
        );
      }
    }
  }

  // Generate session-specific encryption key
  generateSessionKey() {
    const sessionData = [
      navigator.userAgent,
      navigator.language,
      window.screen.width + 'x' + window.screen.height,
      new Date().getTime().toString()
    ].join('|');
    
    return CryptoJS.SHA256(sessionData + 'bluelagoon_session_2024').toString();
  }

  // Generate browser fingerprint for token binding
  generateBrowserFingerprint() {
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      navigator.platform,
      window.screen.width + 'x' + window.screen.height,
      window.screen.colorDepth,
      new Date().getTimezoneOffset(),
      navigator.hardwareConcurrency || 'unknown',
      navigator.deviceMemory || 'unknown'
    ].join('|');
    
    return CryptoJS.SHA256(fingerprint).toString().substring(0, 16);
  }

  // Generate unique request ID
  generateRequestId() {
    return 'req_' + Date.now().toString(36) + Math.random().toString(36).substring(2);
  }

  // Authentication methods
  async login(email, password) {
    const passwordHash = this.hashPassword(password, email);
    
    const response = await this.apiCall('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ 
        email, 
        passwordHash,
        browserFingerprint: this.browserFingerprint,
        clientInfo: {
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          fingerprint: this.browserFingerprint
        }
      })
    });
    
    const result = await response.json();
    
    // Handle successful login (store tokens)
    if (result.success && result.data) {
      this.setTokens(
        result.data.accessToken,
        result.data.refreshToken,
        result.data.user
      );
    }
    
    return result;
  }

  async register(userData) {
    const response = await this.apiCall('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        ...userData,
        clientInfo: {
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      })
    });
    
    return response.json();
  }

  async logout() {
    const refreshToken = this.getRefreshToken();
    
    if (refreshToken) {
      try {
        await this.apiCall('/api/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ refreshToken })
        });
      } catch (error) {
        console.error('Logout API call failed:', error);
      }
    }
    
    this.clearTokens();
    this.stopTokenRefresh();
  }

  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();
    
    if (!refreshToken) {
      return { success: false, error: 'No refresh token available' };
    }
    
    try {
      const response = await this.apiCall('/api/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken })
      });
      
      const result = await response.json();
      
      if (result.success && result.data) {
        this.setTokens(
          result.data.accessToken,
          result.data.refreshToken || refreshToken
        );
        return { success: true };
      }
      
      return { success: false, error: result.error?.message || 'Token refresh failed' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async forgotPassword(email) {
    const response = await this.apiCall('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
    
    return response.json();
  }

  async resetPassword(token, newPassword) {
    const response = await this.apiCall('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword })
    });
    
    return response.json();
  }

  async verifyToken() {
    if (!this.isAuthenticated()) {
      return { success: false, error: 'No valid token' };
    }
    
    const response = await this.apiCall('/api/auth/verify');
    return response.json();
  }

  // Token refresh management
  initializeTokenRefresh() {
    // Check token validity every 2 minutes for faster rotation
    this.refreshInterval = setInterval(() => {
      if (this.isAuthenticated()) {
        const token = this.getAccessToken();
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const currentTime = Math.floor(Date.now() / 1000);
          const timeUntilExpiry = payload.exp - currentTime;
          
          // Refresh if token expires in less than 3 minutes (for 5-minute tokens)
          if (timeUntilExpiry < 180) {
            this.refreshAccessToken();
          }
        } catch (error) {
          console.error('Token parsing error:', error);
        }
      }
    }, 2 * 60 * 1000); // 2 minutes
  }

  stopTokenRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  // Marketplace methods with authentication
  async getMarketplace(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/api/marketplace${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.apiCall(endpoint);
    return response.json();
  }

  async getProperty(id) {
    const response = await this.apiCall(`/api/marketplace/${id}`);
    return response.json();
  }

  async getPropertyDetails(id) {
    const response = await this.apiCall(`/api/properties/${id}`);
    return response.json();
  }

  async placeBid(propertyId, bidAmount) {
    const response = await this.apiCall(`/api/marketplace/${propertyId}/bid`, {
      method: 'POST',
      body: JSON.stringify({ bidAmount })
    });
    
    return response.json();
  }

  async getPropertyPricing(id) {
    const response = await this.apiCall(`/api/marketplace/${id}/pricing`);
    return response.json();
  }

  async getPropertyBids(id) {
    const response = await this.apiCall(`/api/marketplace/${id}/bids`);
    return response.json();
  }

  async getPropertyDocuments(id) {
    const response = await this.apiCall(`/api/properties/${id}/documents`);
    return response.json();
  }

  async getPropertyAnalysis(id) {
    const response = await this.apiCall(`/api/properties/${id}/analysis`);
    return response.json();
  }

  async expressInterest(propertyId, interestData) {
    const response = await this.apiCall(`/api/properties/${propertyId}/interest`, {
      method: 'POST',
      body: JSON.stringify(interestData)
    });
    
    return response.json();
  }

  // Guides methods with authentication
  async getGuides(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/api/guides${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.apiCall(endpoint);
    return response.json();
  }

  async getGuide(id) {
    const response = await this.apiCall(`/api/guides/${id}`);
    return response.json();
  }

  async searchGuides(query, filters = {}) {
    const params = { q: query, ...filters };
    const queryString = new URLSearchParams(params).toString();
    
    const response = await this.apiCall(`/api/guides/search/query?${queryString}`);
    return response.json();
  }

  async accessGuide(id) {
    const response = await this.apiCall(`/api/guides/${id}/access`, {
      method: 'POST'
    });
    
    return response.json();
  }

  // Search methods
  async searchProperties(query, filters = {}) {
    const params = { q: query, ...filters };
    const queryString = new URLSearchParams(params).toString();
    
    const response = await this.apiCall(`/api/properties/search/query?${queryString}`);
    return response.json();
  }

  // Utility methods
  async getFilterOptions() {
    const response = await this.apiCall('/api/marketplace/filters/options');
    return response.json();
  }

  async getGuideCategories() {
    const response = await this.apiCall('/api/guides/meta/categories');
    return response.json();
  }

  // Contact methods
  async submitContact(contactData) {
    const response = await this.apiCall('/api/contact', {
      method: 'POST',
      body: JSON.stringify(contactData)
    });
    
    return response.json();
  }

  async subscribeNewsletter(email, preferences = []) {
    const response = await this.apiCall('/api/contact/newsletter', {
      method: 'POST',
      body: JSON.stringify({ email, preferences })
    });
    
    return response.json();
  }

  async requestCallback(callbackData) {
    const response = await this.apiCall('/api/contact/callback', {
      method: 'POST',
      body: JSON.stringify(callbackData)
    });
    
    return response.json();
  }

  async getContactInfo() {
    const response = await this.apiCall('/api/contact/info');
    return response.json();
  }

  // Health check
  async healthCheck() {
    try {
      const response = await this.apiCall('/api/health');
      return response.json();
    } catch (error) {
      return { status: 'error', message: error.message };
    }
  }
}

// Create and export singleton instance
const authService = new AuthService();
export default authService;

// Export individual methods for convenience
export const {
  login,
  register,
  logout,
  forgotPassword,
  resetPassword,
  verifyToken,
  isAuthenticated,
  getGuides,
  getGuide,
  searchGuides,
  accessGuide,
  getMarketplace,
  getProperty,
  getPropertyDetails,
  placeBid,
  getPropertyPricing,
  getPropertyBids,
  getPropertyDocuments,
  getPropertyAnalysis,
  expressInterest,
  searchProperties,
  getFilterOptions,
  getGuideCategories,
  healthCheck
} = authService;
